import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import MakeLogo from '@/assets/make-logo.svg?react';
import N8NLogo from '@/assets/n8n-logo.svg?react';
import PowerAutomateLogo from '@/assets/power-automate-logo.svg?react';
import ZapierLogo from '@/assets/zapier-logo.svg?react';
import { Text } from '@/components/core';
import { AutomaticIcon, CheckmarkFilledIcon, CloseIcon, TriggerIcon } from '@/components/icons';
import { User } from '@/components/ui';
import { useGetAgentRunVariables } from '@/hooks';
import { toActionId, useGetAction } from '@/hooks/api';

import { Cell, IndentedDiv, Indicator } from './common';
import { EventTooltip, EventTooltipBodyProps } from './EventTooltip';

const ITriggerEventTooltipBody = ({ event, agentId, runId }: EventTooltipBodyProps) => {
  const { get } = useGetAgentRunVariables({ agentId, runId });
  const emailOrigin = useMemo(() => get('emailOrigin') as string | undefined, [get]);
  const triggerSource = useMemo(() => get('triggerSource') as string | undefined, [get]);
  const { data: action } = useGetAction(toActionId(event.actionId));

  const { content } = useMemo(() => {
    let content;
    switch (event.trigger) {
      case 'unknown':
        content = (
          <div className="gap-sm flex flex-col">
            <div className="gap-sm flex items-center">
              <Indicator variant="error" />
              <Text>Document not found</Text>
            </div>
            <IndentedDiv>
              <Text>Document was either deleted or expired due to retention settings.</Text>
            </IndentedDiv>
          </div>
        );
        break;
      case 'manual':
        content = (
          <div className="gap-sm flex items-center">
            <Indicator variant="success" />
            <div className="rounded-xs bg-gray-700 px-1 py-0.5">
              <User userId={event.userId} props={{ img: { className: 'size-4' } }} disableTooltip showName />
            </div>
          </div>
        );
        break;
      case 'api':
        {
          let source;
          switch (triggerSource) {
            case 'zapier':
              source = 'Zapier';
              break;
            case 'make':
              source = 'Make';
              break;
            case 'n8n':
              source = 'n8n';
              break;
            case 'power-automate':
              source = 'Power Automate';
              break;
            default:
              source = 'API';
          }

          content = (
            <div className="flex items-center">
              <Indicator variant="success" />
              <div className="flex rounded-xs bg-gray-700 px-1 py-0.5">
                <Text>Agent was triggered by {source}</Text>
              </div>
            </div>
          );
        }
        break;
      default:
        if (emailOrigin) {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="success" />
                <Text>Email received from</Text>
              </div>
              <IndentedDiv>
                <Text>{emailOrigin}</Text>
              </IndentedDiv>
            </div>
          );
        } else if (action) {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="success" />
                <Text>Document received from</Text>
              </div>
              <IndentedDiv>
                <Text>{action?.name}</Text>
              </IndentedDiv>
            </div>
          );
        } else {
          content = null;
        }
    }
    return { content };
  }, [event, emailOrigin, action, triggerSource]);

  return content;
};

const TriggerEventTooltipBody = memo(ITriggerEventTooltipBody) as typeof ITriggerEventTooltipBody;

export type TriggerEventTooltipProps = {
  agentId: string;
  runId: string;
  event: AgentRunEvent;
};

const ITriggerEventTooltip = ({ agentId, runId, event }: TriggerEventTooltipProps) => {
  const { data: action } = useGetAction(toActionId(event.actionId));
  const { get } = useGetAgentRunVariables({ agentId, runId });
  const triggerSource = useMemo(() => get('triggerSource') as string | undefined, [get]);

  const { heading, icon } = useMemo(() => {
    let heading;
    let icon;
    switch (event.trigger) {
      case 'unknown':
        heading = 'Trigger';
        icon = (
          <Focusable>
            <Cell>
              <CloseIcon className="text-gray-500" />
            </Cell>
          </Focusable>
        );
        break;
      case 'manual':
        heading = 'Manual trigger';
        icon = (
          <Focusable>
            <Cell>
              <div className="relative">
                <User className="size-6" userId={event.userId} disableTooltip />
                <CheckmarkFilledIcon
                  variant="success"
                  className="absolute right-0 bottom-0 z-50 size-3 translate-x-0.5 translate-y-0.5 rounded-full bg-white"
                />
              </div>
            </Cell>
          </Focusable>
        );
        break;
      case 'api':
        {
          let innerIcon;

          switch (triggerSource) {
            case 'zapier':
              heading = 'Triggered by Zapier';
              innerIcon = <ZapierLogo className="size-4 text-gray-500" />;
              break;
            case 'make':
              heading = 'Triggered by Make';
              innerIcon = <MakeLogo className="size-4 text-gray-500" />;
              break;
            case 'n8n':
              heading = 'Triggered by n8n';
              innerIcon = <N8NLogo className="size-4 text-gray-500" />;
              break;
            case 'power-automate':
              heading = 'Triggered by Power Automate';
              innerIcon = <PowerAutomateLogo className="size-4 text-gray-500" />;
              break;
            default:
              heading = 'Automatic trigger';
              innerIcon = <AutomaticIcon className="size-4 text-gray-500" />;
          }

          icon = (
            <Focusable>
              <Cell>
                <div className="relative">
                  {innerIcon}
                  <CheckmarkFilledIcon
                    variant="success"
                    className="absolute right-0 bottom-0 z-50 size-3 translate-x-1.5 translate-y-1.5 rounded-full bg-white"
                  />
                </div>
              </Cell>
            </Focusable>
          );
        }
        break;
      default:
        heading = action ? `Triggered by ${action.name}` : 'Automatic trigger';
        icon = (
          <Focusable>
            <Cell>
              <div className="relative">
                <TriggerIcon className="size-4" functionId={action?.functionId} />
                <CheckmarkFilledIcon
                  variant="success"
                  className="absolute right-0 bottom-0 z-50 size-3 translate-x-1.5 translate-y-1.5 rounded-full bg-white"
                />
              </div>
            </Cell>
          </Focusable>
        );
    }

    return { heading, icon };
  }, [action, event, triggerSource]);

  return (
    <EventTooltip
      event={event}
      agentId={agentId}
      runId={runId}
      heading={heading}
      Body={TriggerEventTooltipBody}
      timestamp={event.timestamp}
      icon={icon}
    />
  );
};

export const TriggerEventTooltip = memo(ITriggerEventTooltip) as typeof ITriggerEventTooltip;
